/**
 * Telemetry Service — Display Client
 * ─────────────────────────────────────────────────────────────────────────────
 * Reports Web Vitals and custom metrics to the server.
 * Endpoint: POST {VITE_SERVER_BASE_URL}/api/telemetry/web-vitals (server can ignore if not implemented)
 */

import logger from "../utils/logger";

const SERVER_URL = import.meta.env.VITE_SERVER_BASE_URL || "";
const TELEMETRY_ENDPOINT = SERVER_URL ? `${SERVER_URL}/api/telemetry/web-vitals` : null;

const sendTelemetry = (metric, deviceId) => {
  if (!TELEMETRY_ENDPOINT) return;

  const payload = JSON.stringify({
    name: metric.name,
    value: metric.value,
    rating: metric.rating,
    deviceId: deviceId || localStorage.getItem("deviceId") || null,
    timestamp: Date.now(),
    url: window.location.href,
  });

  // Use beacon API for reliability (works even on page unload)
  if (navigator.sendBeacon) {
    try {
      const blob = new Blob([payload], { type: "application/json" });
      navigator.sendBeacon(TELEMETRY_ENDPOINT, blob);
      return;
    } catch (e) {
      /* fall through */
    }
  }

  // Fallback: fetch with keepalive
  fetch(TELEMETRY_ENDPOINT, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: payload,
    keepalive: true,
  }).catch((err) => logger.debug("[Telemetry] Send failed", { error: err.message }));
};

/**
 * Initialize Web Vitals reporting using `web-vitals` package.
 * Dynamically imported so it doesn't block initial render.
 */
export const initWebVitals = async () => {
  try {
    const { onCLS, onINP, onFCP, onLCP, onTTFB } = await import("web-vitals");
    const deviceId = localStorage.getItem("deviceId");

    onCLS((metric) => sendTelemetry(metric, deviceId));
    onINP((metric) => sendTelemetry(metric, deviceId));
    onFCP((metric) => sendTelemetry(metric, deviceId));
    onLCP((metric) => sendTelemetry(metric, deviceId));
    onTTFB((metric) => sendTelemetry(metric, deviceId));

    logger.info("[Telemetry] Web Vitals reporting started");
  } catch (err) {
    logger.warn("[Telemetry] Failed to load web-vitals", { error: err.message });
  }
};
